import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Grid,
  Tooltip
} from '@mui/material';
import { useAuth } from '../../AuthContext';

// Alert type specific configuration components
import PriceAlertConfig from './configurations/PriceAlertConfig';
import CongressionalTradeConfig from './configurations/CongressionalTradeConfig';
import NewsAlertConfig from './configurations/NewsAlertConfig';
import InsiderTradeConfig from './configurations/InsiderTradeConfig';
import MarketEventConfig from './configurations/MarketEventConfig';

const AlertConfigurationForm = ({ alertData, onUpdateAlertData }) => {
  const { type, name, triggerType } = alertData;
  const { user } = useAuth();
  
  // Check if user is on free plan
  const isFreePlan = !user?.plan?.name || user.plan.name.toLowerCase() === 'free';
  
  // Price alerts are always "once"
  const isPriceAlert = type === 'priceAlert';
  
  // Disable toggle if price alert or free plan
  const isToggleDisabled = isPriceAlert || isFreePlan;

  // Force triggerType to 'once' for price alerts
  useEffect(() => {
    if (isPriceAlert && triggerType !== 'once') {
      onUpdateAlertData({
        triggerType: 'once'
      });
    }
  }, [isPriceAlert, triggerType, onUpdateAlertData]);

  // Get title based on alert type
  const getAlertTypeTitle = () => {
    switch (type) {
      case 'priceAlert':
        return 'Price Alert Configuration';
      case 'congressTrade':
        return 'Congressional Trading Alert Configuration';
      case 'newsAlert':
        return 'News Alert Configuration';
      case 'insiderTrade':
        return 'Insider Trading Alert Configuration';
      case 'marketEvent':
        return 'Market Event Alert Configuration';
      default:
        return 'Alert Configuration';
    }
  };

  // Handle change for the alert name
  const handleNameChange = (e) => {
    onUpdateAlertData({
      name: e.target.value
    });
  };

  // Toggle between continuous and one-time alerts
  const handleTriggerTypeChange = (e) => {
    if (isToggleDisabled) return;
    
    onUpdateAlertData({
      triggerType: e.target.checked ? 'continuous' : 'once'
    });
  };

  // Get help text for trigger type
  const getTriggerTypeHelpText = () => {
    if (isPriceAlert) {
      return 'Price alerts are one-time notifications that trigger once when conditions are met';
    }
    
    if (isFreePlan) {
      return 'Upgrade to Basic or Premium plan to enable continuous alerts';
    }
    
    return triggerType === 'continuous'
      ? 'Alert will trigger continuously when conditions are met'
      : 'Alert will trigger once and then deactivate';
  };

  // Render appropriate configuration component based on alert type
  const renderAlertTypeConfig = () => {
    switch (type) {
      case 'priceAlert':
        return (
          <PriceAlertConfig
            alertData={alertData}
            onUpdateAlertData={onUpdateAlertData}
          />
        );
      case 'congressTrade':
        return (
          <CongressionalTradeConfig
            alertData={alertData}
            onUpdateAlertData={onUpdateAlertData}
          />
        );
      case 'newsAlert':
        return (
          <NewsAlertConfig
            alertData={alertData}
            onUpdateAlertData={onUpdateAlertData}
          />
        );
      case 'insiderTrade':
        return (
          <InsiderTradeConfig
            alertData={alertData}
            onUpdateAlertData={onUpdateAlertData}
          />
        );
      case 'marketEvent':
        return (
          <MarketEventConfig
            alertData={alertData}
            onUpdateAlertData={onUpdateAlertData}
          />
        );
      default:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" color="error">
              Please select an alert type to continue
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        {getAlertTypeTitle()}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Alert Name"
              variant="outlined"
              fullWidth
              value={name}
              onChange={handleNameChange}
              required
              helperText="Give your alert a descriptive name to help you identify it later"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                p: { xs: 1, sm: 2 }
              }}
            >
              <Tooltip 
                title={isFreePlan && !isPriceAlert ? "Available for Basic and Premium plans" : ""}
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={triggerType === 'continuous'}
                      onChange={handleTriggerTypeChange}
                      color="primary"
                      disabled={isToggleDisabled}
                    />
                  }
                  label={
                    <Typography 
                      variant="body1" 
                      fontWeight="medium"
                      color={isToggleDisabled ? 'text.disabled' : 'text.primary'}
                    >
                      {triggerType === 'continuous' ? 'Continuous alert' : 'One-time alert'}
                    </Typography>
                  }
                  disabled={isToggleDisabled}
                />
              </Tooltip>
              <Typography
                variant="body2"
                color={isToggleDisabled && isFreePlan ? 'warning.main' : 'text.secondary'}
                sx={{ mt: 1 }}
              >
                {getTriggerTypeHelpText()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Render the appropriate configuration form */}
      {renderAlertTypeConfig()}
    </Box>
  );
};

export default AlertConfigurationForm;